import { Divider, Stack } from "@mui/material";
import Header from "./Header";
import ImprovementAction from "./ImprovementAction";
import Risk from "./Risk";
import SleepJudge from "./SleepJudge";

import { PlotType } from "@/components/report/commons/SleepDiaryChart";
import { usePageUserType } from "@/utils/router";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { BeforeCheckReportDialog } from "./BeforeCheckReportDialog";
import { RiskTitle, RiskType, ThreeLevelScalesType } from "./commons";
import { SleepJudgeTitle } from "./SleepJudge/commons";
import { ChartData } from "./SleepJudge/DeviceMeasurementData";
import TotalJudge from "./TotalJudge";
import YourImprovementMethod, { YourJudgement } from "./YourImprovementMethod";

const LIFE_STYLE_TYPE_NAME_MAP = {
  nap: "仮眠のタイミングと長さ",
  blue_light: "寝る前のブルーライト",
  alcohol: "お酒の飲みすぎ",
  caffeine: "寝る前のカフェイン",
  night_meal: "夜食",
  only_shower: "シャワーのみの入浴",
  hot_bath: "寝る前の熱いお風呂",
  room_light_sleeping: "寝ている間の照明",
  room_light: "寝る前の明るい照明",
  morning_light: "朝の光不足",
  before_sleep_mind: "寝床での考え事",
  body_condition: "体の調子",
  exercise: "運動不足",
} as const;
export type LifeStyleType = keyof typeof LIFE_STYLE_TYPE_NAME_MAP;

const EACH_DAY_RESULT_KEY_NAME_MAP = {
  sleep_length: "睡眠の長さ",
  sleep_quality: "睡眠の質",
  sleep_rhythm: "睡眠のリズム",
  ...LIFE_STYLE_TYPE_NAME_MAP,
} as const;
export type EachDayResultType = keyof typeof EACH_DAY_RESULT_KEY_NAME_MAP;

export const SOLUTION_TYPE_NAME_MAP = {
  // 睡眠に関連するもの
  efficiency: "睡眠効率低下の一般的な原因を知る",
  awakening: "中途覚醒が増える一般的な原因を知る",
  toilet: "トイレに起きる原因を学ぶ",
  initiating_sleep: "寝付きが悪くなる一般的な原因を知る",
  sleep_time: "睡眠時間の重要性を知る",
  ideas_for_longer_sleep: "睡眠時間を確保するアイデアを知る",
  snoring: "いびきの改善方法を学ぶ",
  premature_morning_awakening: "早く目が覚めてしまう一般的な原因を知る",
  contact: "相談先を知る",
  dream: "夢に関して学ぶ",
  turn_over: "寝返りの改善方法を学ぶ",

  // 生活習慣に関連するもの
  nap: "睡眠と仮眠の関係を学ぶ",
  blue_light: "睡眠とスマホの関係を学ぶ",
  alcohol: "睡眠と飲酒の関係を学ぶ",
  caffeine: "睡眠とカフェインの関係を学ぶ",
  night_meal: "睡眠と夜食の関係を学ぶ",
  bath: "睡眠と入浴の関係を学ぶ",
  room_light: "睡眠と部屋の明かりの関係を学ぶ",
  morning_light: "睡眠と朝の光の関係を学ぶ",
  before_sleep_mind: "寝床での考え事",
  body_condition: "睡眠と体の調子の関係を学ぶ",
  exercise: "睡眠と運動の関係を学ぶ",
  general: "一般的な睡眠に影響する生活習慣を知る",
} as const;
export type SolutionType = keyof typeof SOLUTION_TYPE_NAME_MAP;

export type EmphasizedChartData = {
  type: "length" | "quality" | "rhythm";
  average_wake_up_time: string | null;
  chart_data: {
    date: string;
    arise_time: string;
    sleep_data: {
      from: string;
      to: string;
      type: PlotType;
    }[];
  }[];
};

export type MeasurementPeriodResult = {
  sleep_length_result: SleepJudgeTitle;
  sleep_length_summary: string;
  sleep_quality_result: SleepJudgeTitle;
  sleep_quality_summary: string;
  sleep_quality_impact_scores: ImpactScore[];
  sleep_rhythm_result: SleepJudgeTitle;
  sleep_rhythm_summary: string;
};

export type RecommendedSleepTime = {
  /** 年齢 */
  age_range: string;
  /** 時間 */
  recommended_sleep_time_low: number;
  recommended_sleep_time_high: number;
};

export type BasisTableCell = {
  type: "level" | "check" | "text";

  // type="level" の場合に使用する項目
  level?: ThreeLevelScalesType;
  // type="check" の場合に使用する項目
  is_checked?: boolean;
  // type="text" の場合に使用する項目
  is_bad?: boolean;
  text?: string;
};

export type ImpactScore = {
  name: LifeStyleType;
  caption: string;
  value: number | null;
  each_day_result: (ThreeLevelScalesType | null)[];
};

export type IssueFromUser = {
  title: string;
  judgement: YourJudgement | null;
  judgement_summary: string | null;
  paragraphs: {
    description_lines: string[] | null;
    solution: {
      caption: string;
      type: SolutionType;
    } | null;
  }[];
  basis_table: {
    title: string;
    item_names: string[];
    cells: (BasisTableCell | null)[][];
    last_row_is_aggregation_result: boolean;
  } | null;
  lifestyle_scores_table: {
    title: string;
    sleep_factor:
      | "睡眠の質"
      | "睡眠効率"
      | "中途覚醒時間"
      | "入眠潜時"
      | "早すぎる目覚め";
    impact_scores: ImpactScore[];
    exists_most_impactful_lifestyle: boolean;
    message_for_no_impactful_lifestyle: string | null;
  } | null;
};

export interface WebReport2SASRiskData {
  type: RiskType;
  title: RiskTitle;
  summary: string | null;
  description_lines: string[];
  ODI_3_percent: number | null;
}

export interface WebReport2InsomniaRiskData {
  type: RiskType;
  title: RiskTitle;
  summary: string | null;
  description_lines: string[];
  AIS_score: number | null;
}
export interface WebReport2Data {
  publish_version: string;
  name: string;
  period: [string, string];
  chart_data: ChartData[];
  emphasized_chart_data: EmphasizedChartData | null;
  grade: {
    value: string;
    summary: string;
    description_lines: string[];
    previous_value: string;
    previous_summary: string;
    previous_uuid: string;
  };
  improvement_action: {
    recommended_to_visit_medical_institution: boolean;
    summary_lines: string[] | null;
    description_lines: string[] | null;
    learning: {
      caption: string;
      type: SolutionType;
    } | null;
  } | null;
  date_list: string[];
  measurement_period_result: MeasurementPeriodResult;
  /** 起床予定時刻と覚醒時刻のズレ */
  wakeup_time_gap: (string | null)[];
  /** 睡眠時間（分) */
  sleep_time: (string | null)[];
  /** 推奨睡眠時間 */
  recommended_sleep_time: RecommendedSleepTime;
  each_day_result: Record<EachDayResultType, (ThreeLevelScalesType | null)[]>;
  aggregation: {
    waso_in_minutes: (number | null)[];
  };
  lifestyle_impact: {
    most_impactful_lifestyle: LifeStyleType;
    impact_score: number;
  } | null;
  risks: {
    SAS: WebReport2SASRiskData | null;
    insomnia: WebReport2InsomniaRiskData | null;
  };
  issue_from_user: IssueFromUser | null;
}

export interface WebReport2Props {
  uuid: string;
  data: WebReport2Data;
}

function excludeNap<
  T extends {
    arise_time: string | null;
    sleep_data: {
      from: string;
      to: string;
      type: PlotType;
    }[];
  }[],
>(chart_data: T) {
  return chart_data.map((c) => ({
    ...c,
    sleep_data: c.sleep_data.filter((d) => d.type !== "nap"),
  })) as T;
}

export default function WebReport2({ uuid, data: _data }: WebReport2Props) {
  const location = useLocation();
  const userType = usePageUserType();
  const query = new URLSearchParams(location.search);
  const [openBeforeCheckReportDialog, setOpenBeforeCheckReportDialog] =
    useState(query.get("open_before_check_report_dialog") === "true");

  const data = {
    ..._data,
    chart_data: excludeNap(_data.chart_data),
    emphasized_chart_data:
      _data.emphasized_chart_data == null
        ? null
        : {
            ..._data.emphasized_chart_data,
            chart_data: excludeNap(_data.emphasized_chart_data.chart_data),
          },
  };
  return (
    <Stack sx={{ maxWidth: "1088px" }}>
      <BeforeCheckReportDialog
        open={openBeforeCheckReportDialog}
        close={() => {
          setOpenBeforeCheckReportDialog(false);
        }}
        chart_data={data.chart_data}
        useRecommend={data.risks.SAS != null || data.risks.insomnia != null}
      />
      <Header
        data={{ name: data.name, period: data.period as [string, string] }}
      />
      {/** 総合判定 */}
      <TotalJudge {...data.grade} />
      {/** 改善アクション */}
      {data.improvement_action && (
        <ImprovementAction
          recommended_sleep_time={data.recommended_sleep_time}
          sleep_time={data.sleep_time}
          wakeup_time_gap={data.wakeup_time_gap}
          sleep_quality_impact_scores={
            data.measurement_period_result.sleep_quality_impact_scores
          }
          each_day_result={data.each_day_result}
          emphasized_chart_data={data.emphasized_chart_data}
          {...data.improvement_action}
          {...data.each_day_result}
        />
      )}
      <Divider sx={{ borderWidth: "8px", borderColor: "#E8E6E2" }} />
      {/** 疾患のリスク */}
      {(data.risks.SAS != null || data.risks.insomnia != null) && (
        <>
          <Risk
            SAS={data.risks.SAS}
            insomnia={
              data.risks.insomnia != null
                ? {
                    grade: data.grade.value,
                    ...data.risks.insomnia,
                    ...data.measurement_period_result,
                  }
                : null
            }
          />
          <Divider sx={{ borderWidth: "8px", borderColor: "#E8E6E2" }} />
        </>
      )}
      {/** 睡眠の判定 */}
      <SleepJudge
        uuid={uuid}
        userType={userType}
        {...data.measurement_period_result}
        {...data.each_day_result}
        chart_data={data.chart_data}
      />
      <Divider sx={{ borderWidth: "8px", borderColor: "#E8E6E2" }} />
      <YourImprovementMethod
        userType={userType}
        uuid={uuid}
        issue_from_user={data.issue_from_user}
      />
    </Stack>
  );
}
